import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Toast, { useToast } from '../components/Toast';
import './JobApply.css';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES  = ['application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

function JobApply() {
  const { jobId }   = useParams();
  const navigate    = useNavigate();
  const fileRef     = useRef(null);
  const { toast, showToast, clearToast } = useToast();

  const [job, setJob]         = useState(null);
  const [jobError, setJobError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [fileObj, setFileObj]       = useState(null);
  const [fileError, setFileError]   = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    currentCTC: '', expectedCTC: '', noticePeriod: '',
  });

  useEffect(() => {
    supabase
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single()
      .then(({ data, error }) => {
        if (error || !data) setJobError(true);
        else setJob(data);
      })
      .catch(() => setJobError(true));
  }, [jobId]);

  if (jobError) {
    navigate('/careers');
    return null;
  }

  if (!job) {
    return (
      <div className="job-apply">
        <section className="job-apply-hero">
          <div className="job-apply-container" style={{ textAlign: 'center', color: '#64748b', padding: '4rem 0' }}>
            Loading…
          </div>
        </section>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');
    if (!file) { setFileObj(null); return; }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only PDF, DOC, or DOCX files are allowed.');
      setFileObj(null);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError('File must be under 5 MB.');
      setFileObj(null);
      e.target.value = '';
      return;
    }
    setFileObj(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fileObj) { setFileError('Please attach your resume.'); return; }

    setSubmitting(true);
    try {
      // 1. Upload resume to Supabase Storage
      const ext      = fileObj.name.split('.').pop();
      const filePath = `resumes/${jobId}/${Date.now()}_${formData.name.replace(/\s+/g,'_')}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from('job-applications')
        .upload(filePath, fileObj, { contentType: fileObj.type, upsert: false });

      if (uploadErr) throw new Error(uploadErr.message);

      // 2. Get public URL
      const { data: urlData } = supabase.storage
        .from('job-applications')
        .getPublicUrl(filePath);

      // 3. Save application record
      const { error: insertErr } = await supabase.from('job_applications').insert([{
        job_id:        job.id,
        name:          formData.name,
        email:         formData.email,
        phone:         formData.phone,
        current_ctc:   formData.currentCTC,
        expected_ctc:  formData.expectedCTC,
        notice_period: formData.noticePeriod,
        resume_url:    urlData?.publicUrl ?? filePath,
        resume_file:   fileObj.name,
      }]);

      if (insertErr) throw new Error(insertErr.message);

      setSubmitted(true);
    } catch (err) {
      showToast(err.message || 'Submission failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="job-apply">
        <section className="job-apply-hero">
          <div className="job-apply-container" style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>✓</div>
            <h2 style={{ color: '#4ade80', marginBottom: '0.75rem' }}>Application Submitted!</h2>
            <p style={{ color: '#94a3b8', maxWidth: 400, margin: '0 auto 2rem' }}>
              Thank you for applying for <strong style={{ color: '#fff' }}>{job.title}</strong>. We'll review your application and get back to you soon.
            </p>
            <button className="btn-primary" onClick={() => navigate('/careers')}>
              Back to Careers
            </button>
          </div>
        </section>
        <Toast {...toast} onClose={clearToast} />
      </div>
    );
  }

  return (
    <div className="job-apply">
      <Toast {...toast} onClose={clearToast} />

      <section className="job-apply-hero">
        <div className="job-apply-container">
          <h1 className="job-apply-title">{job.title}</h1>
          <div className="job-meta">
            {job.type     && <span>{job.type}</span>}
            {job.location && <span>{job.location}</span>}
            {job.experience && <span>{job.experience}</span>}
          </div>
        </div>
      </section>

      <section className="job-apply-main">
        <div className="job-apply-container">
          <div className="job-apply-grid">
            <div className="job-description">
              <h2>Job Description</h2>
              <p>{job.description}</p>
            </div>

            <div className="application-form-wrapper">
              <h2>Apply for this Position</h2>
              <form className="application-form" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="ja-name">Full Name *</label>
                  <input id="ja-name" type="text" name="name" value={formData.name}
                    onChange={handleChange} required placeholder="John Doe" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-email">Email *</label>
                  <input id="ja-email" type="email" name="email" value={formData.email}
                    onChange={handleChange} required placeholder="john@example.com" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-phone">Phone *</label>
                  <input id="ja-phone" type="tel" name="phone" value={formData.phone}
                    onChange={handleChange} required placeholder="+91 9876543210" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-ctc">Current CTC *</label>
                  <input id="ja-ctc" type="text" name="currentCTC" value={formData.currentCTC}
                    onChange={handleChange} required placeholder="e.g., 12 LPA" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-ectc">Expected CTC *</label>
                  <input id="ja-ectc" type="text" name="expectedCTC" value={formData.expectedCTC}
                    onChange={handleChange} required placeholder="e.g., 18 LPA" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-notice">Notice Period *</label>
                  <input id="ja-notice" type="text" name="noticePeriod" value={formData.noticePeriod}
                    onChange={handleChange} required placeholder="e.g., 30 days, Immediate" />
                </div>

                <div className="form-group">
                  <label htmlFor="ja-resume">Resume *</label>
                  <input id="ja-resume" type="file" ref={fileRef}
                    accept=".pdf,.doc,.docx" onChange={handleFileChange} required />
                  {fileError
                    ? <small style={{ color: '#f87171', marginTop: '0.35rem', display: 'block' }}>{fileError}</small>
                    : <small style={{ color: '#94a3b8', marginTop: '0.35rem', display: 'block' }}>PDF, DOC, or DOCX — max 5 MB</small>
                  }
                  {fileObj && (
                    <small style={{ color: '#4ade80', marginTop: '0.25rem', display: 'block' }}>
                      ✓ {fileObj.name}
                    </small>
                  )}
                </div>

                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default JobApply;
